import React, { useState, useContext } from 'react'
import { get, isEmpty } from 'lodash'
import './styles.scss'

import GeneralInput from '../../../../../components/GeneralInput'
import Button from '../../../../../components/Button'
import UserCard from '../../../../../components/UserCard'
import UserExtraValues, { IUserData } from '../../../../../components/UserExtraValues'

import { registerUser } from '../../../../../services/auth'

import CONTRACT_ACTIONS from '../../../../../store/actions/contractActions'
import contractContext from '../../../../../store/contexts/contractContext'

import { cleanupPhone, filterString } from '../../../../../helpers/cleanup'
import { formatUserData } from '../../../../../helpers/userData'

import { PublicUserData } from 'types/user'
import { getUserPublicData } from 'services/contract'
import ErrorComponent from 'components/ErrorComponent'

interface LocalUserPublicData extends PublicUserData {
  email: string
}

const DEFAULT_PASSWORD = 'mudar123'

const INITIAL_USER_FORM = {
  email: '',
}

export default function TenantRegister() {
  const token = localStorage.getItem('accessToken')
  const {
    contract: { contractDispatch, users },
    handleStepChange,
    setIsLoading,
  } = useContext(contractContext)

  const [usersArray, setUsersArray] = useState<LocalUserPublicData[]>(users)
  const [isRegistering, setIsRegistering] = useState<boolean>(isEmpty(users))
  const [registerStep, setRegisterStep] = useState(0)
  const [userExtraData, setUserExtraData] = useState({
    name: { label: 'Nome', mandatory: true, value: '', error: '' },
    phone: { label: 'Telefone', mandatory: true, value: '', error: '' },
    cpf: { label: 'CPF', mandatory: true, value: '', error: '' },
  })
  const [userForm, setUserForm] = useState(INITIAL_USER_FORM)
  const [inputErrors, setInputErrors] = useState(INITIAL_USER_FORM)
  const [errors, setErrors] = useState<string[]>([])

  const onChangeUserForm = (input: string, value: string) => {
    if (inputErrors[input]) {
      clearInputError(input)
    }
    setUserForm((prevForm) => ({ ...prevForm, [input]: value }))
  }

  const clearInputError = (input: string) => {
    setInputErrors((prev) => ({ ...prev, [input]: '' }))
  }

  const resetUserData = () => {
    const resetedUserData = {}

    Object.keys(userExtraData).forEach((property) => {
      resetedUserData[property] = { label: property, value: '' }
    })

    //@ts-ignore
    setUserExtraData(resetedUserData)
  }

  const validateUserForm = () => {
    let isValid = true

    if (!userForm.email) {
      setInputErrors((prev) => ({ ...prev, email: 'E-mail é um campo obrigatório' }))
      isValid = false
    }

    if (userForm.email && userForm.email.indexOf('@') === -1) {
      setInputErrors((prev) => ({ ...prev, email: 'Por favor insira um email válido' }))
      isValid = false
    }

    if (usersArray.some(({ email }) => userForm.email === email)) {
      setInputErrors((prev) => ({ ...prev, email: 'Usuário já consta na lista de inquilinos' }))
      isValid = false
    }

    return isValid
  }

  const handleSearchUser = async () => {
    const isValid = validateUserForm()

    if (!isValid) {
      return
    }

    setIsLoading(true)

    try {
      const userData: PublicUserData = await getUserPublicData({ token: token || '', email: userForm.email })
      setUsersArray((prevState) => [...prevState, { ...userData, email: userForm.email }])

      resetUserData()
      setIsRegistering(false)
      setErrors([])
      setRegisterStep(1)
    } catch (err) {
      setRegisterStep(1)
    }
    setIsLoading(false)
  }

  const setExtraDataError = (key: keyof IUserData, error: string) => {
    setUserExtraData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        error,
      },
    }))
  }

  const handleExtraUserDataErrors = () => {
    let hasError = false

    Object.keys(userExtraData).forEach((property) => {
      const key = property as keyof IUserData
      if (userExtraData[property].mandatory && !userExtraData[property].value) {
        setExtraDataError(key, `O campo ${userExtraData[property].label} é obrigatório`)
        hasError = true
      }
    })

    if (filterString(userExtraData.cpf.value, ['.', '-', '_']).length < 11) {
      setExtraDataError('cpf', 'Insira um CPF válido')
      hasError = true
    }

    return hasError
  }

  const createUser = async () => {
    const hasErrors = handleExtraUserDataErrors()

    if (hasErrors) {
      return
    }

    setIsLoading(true)

    const newUser = {
      name: userExtraData.name.value,
      email: userForm.email,
      phone: cleanupPhone(userExtraData.phone.value),
      password: DEFAULT_PASSWORD,
      ...formatUserData(userExtraData),
    }

    const res = await registerUser(newUser)
    const user = res.data
    setIsLoading(false)

    setUsersArray((prevState) => [...prevState, user])
    resetUserData()
    setIsRegistering(false)
  }

  const handleNextStep = async () => {
    contractDispatch({
      type: CONTRACT_ACTIONS.SET_USERS,
      value: usersArray,
    })
    handleStepChange('BankData')
  }

  const handleActionButton = async () => {
    if (isRegistering) {
      switch (registerStep) {
        case 0:
          await handleSearchUser()
          break
        case 1:
          await createUser()
          break
      }
      return
    }

    await handleNextStep()
  }

  const handleRegisterNewUser = () => {
    setUserForm(INITIAL_USER_FORM)
    setIsRegistering(true)
    setRegisterStep(0)
  }

  const handleDeleteUser = (userCardData) => {
    const filteredUsersArray = usersArray.filter((user) => user.id !== userCardData.id)
    setUsersArray(filteredUsersArray)
  }

  const handleUserData = ({ value, label }: { value: any; label: keyof IUserData }) => {
    setUserExtraData((prev) => ({ ...prev, [label]: { ...prev[label], value } }))

    if (userExtraData[label].error) {
      clearExtraDataError(label)
    }
  }

  const clearExtraDataError = (key: keyof IUserData) => {
    setUserExtraData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        error: '',
      },
    }))
  }

  const [primaryButton, secondaryButton] = (() => {
    if (isRegistering) {
      if (registerStep === 1) {
        return [{ text: 'CADASTRAR' }, { text: 'Voltar', style: {} }]
      }

      return [{ text: 'BUSCAR' }, { text: 'Voltar', style: { visibility: 'hidden' } }]
    }

    return [{ text: 'CONTINUAR' }, { text: 'ADICIONAR OUTRO', style: {} }]
  })()

  const handleUserRegister = () => {
    switch (registerStep) {
      case 0:
        return (
          <>
            <p>
              Insira os dados do morador que fará parte do contrato. O Morador vai receber o contrato para
              assinar e receberá cobranças por esse email.
            </p>
            <GeneralInput
              handleChange={(e) => onChangeUserForm('email', get(e, 'target.value', '').toLowerCase())}
              label='E-mail'
              value={userForm.email}
              error={inputErrors.email}
            />
          </>
        )
      case 1:
        return (
          <>
            <UserExtraValues
              userType='tenant'
              userData={userExtraData}
              handleData={handleUserData}
              subtitle={'Não se preocupe, é coisa rápida e você só vai precisar fazer uma vez!'}
            />
          </>
        )
    }
  }

  return (
    <div>
      {isRegistering ? (
        handleUserRegister()
      ) : (
        <>
          {usersArray.map((user) => (
            <UserCard
              user={user}
              key={`${user.id}`}
              className={'vertical-spacer'}
              onClickDelete={handleDeleteUser}
            />
          ))}
        </>
      )}

      <div style={{ margin: '16px 0' }}>
        <ErrorComponent errors={errors} />
      </div>
      <div className='end-aligner'>
        <div className='button-footer'>
          <div style={secondaryButton.style}>
            {/* @ts-ignore */}
            <Button
              title={secondaryButton.text}
              onClick={isRegistering ? () => setRegisterStep(0) : handleRegisterNewUser}
            />
          </div>
          <Button
            title={primaryButton.text}
            onClick={handleActionButton}
            disabled={usersArray.length === 0 && !isRegistering}
          />
          {!!usersArray.length && registerStep === 0 && isRegistering && (
            <Button
              title={'Ver morador(es)'}
              onClick={() => {
                setIsRegistering(false)
                setErrors([])
              }}
              style={{ marginLeft: 10 }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
